import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Download, User, Filter, CheckCircle, Edit2, Save, X, AlertCircle } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { invoiceService } from '@/services/invoiceService';
import { configService } from '@/services/configService';
import { UserService } from '@/services/userService';
import {
  Invoice,
  InvoiceListParams
} from '@/types/invoice';

// Helper function to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

export const BUReview = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editValues, setEditValues] = useState<{
    buRemarks: string, 
    donorId: string, 
    donorType: string,
    grantType: string,
    budgetId: string,
    departmentId: string
  }>({
    buRemarks: '', 
    donorId: '', 
    donorType: '',
    grantType: '',
    budgetId: '',
    departmentId: ''
  });
  
  const { showSuccess, showError } = useNotification();
  const userService = new UserService();

  // API query filters
  const [filters, setFilters] = useState<InvoiceListParams>({
    page: 1,
    page_size: 10,
    month_year: getCurrentMonth()
  });

  // Fetch application roles — same query key as PnC Review so the two tabs
  // share the cache (a single roles fetch covers both). Kept in sync with
  // that surface deliberately: PnC introduced this exact filter pattern
  // and BU mirrors it now per the ticket's acceptance criteria.
  const {
    data: rolesData,
    isLoading: rolesLoading,
  } = useQuery({
    queryKey: ['application-roles'],
    queryFn: () => userService.getApplicationRoles(),
    staleTime: 5 * 60 * 1000,
  });

  // Prepare role options for dropdown — mirror of PnC's normalisation so
  // both the string-array and {id, title, description} shapes render.
  const roleOptions = [
    { value: '', label: 'All Roles' },
    ...(rolesData?.roles || []).map((role, index) => {
      if (typeof role === 'string') {
        return { value: role, label: role };
      }
      return {
        value: role.id || `role-${index}`,
        label: role.title || role.id || `Role ${index + 1}`,
      };
    }),
  ];

  const programs = [
    { value: '', label: 'All Programs' },
    { value: 'TBTP', label: 'The Billion Tree Project' },
    { value: 'WM', label: 'Waste Management' },
    { value: 'WE', label: 'Women Equity' },
    { value: 'LL', label: 'Learn Labs' },
    { value: 'SD', label: 'Sports for Development' },
    { value: 'RF', label: 'Regenerative Farming' },
    { value: 'YOGA', label: 'Yoga' },
  ];

  const stateOptions = [
    { value: '', label: 'All States' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Approved By PnC', label: 'Approved By PnC' },
    { value: 'Approved By BU', label: 'Approved By BU' },
  ];

  const pageSizeOptions = [
    { value: 10, label: '10 per page' },
    { value: 50, label: '50 per page' },
    { value: 100, label: '100 per page' },
  ];

  // Update filters when search, page, program, role, state, or page size changes
  useEffect(() => {
    const newFilters = {
      page: currentPage,
      page_size: pageSize,
      q: searchQuery?.trim() || undefined,
      program: selectedProgram || undefined,
      role: selectedRole || undefined,
      state: selectedState || undefined,  // Use 'state' parameter with original values
      month_year: selectedMonth || undefined
    };

    setFilters(newFilters);
  }, [searchQuery, currentPage, selectedProgram, selectedRole, selectedState, selectedMonth, pageSize]);

  // Fetch invoices using React Query - BU section sees invoices approved by PnC
  const {
    data: invoicesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['bu-invoices', filters],
    queryFn: () => invoiceService.getInvoices('bu', filters),
    placeholderData: (previousData) => previousData,
  });

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleExportData = async () => {
    try {
      const filename = `bu-invoices-${selectedMonth}-${new Date().toISOString().split('T')[0]}.csv`;
      // Only pass month_year filter for export, remove pagination
      const exportFilters = {
        month_year: selectedMonth || undefined
      };
      await invoiceService.exportInvoices('bu', exportFilters, filename);
      showSuccess('Export Data', 'BU invoice data exported successfully.');
    } catch (error) {
      showError('Export Failed', 'Failed to export BU data. Please try again.');
    }
  };

  const startEditing = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    
    // For grant type, the invoice.grant_type is already the ID
    let grantTypeId = invoice.grant_type || '';
    
    setEditValues({
      buRemarks: invoice.bu_remarks || '',
      donorId: invoice.donor_id?.toString() || '',
      donorType: invoice.donor_type || '',
      grantType: grantTypeId,
      budgetId: invoice.budget_id || '', // budget_id is already a string
      departmentId: invoice.department_id || '' // department_id is already a string
    });
  };

  const cancelEditing = () => {
    setEditingInvoice(null);
    setEditValues({
      buRemarks: '', 
      donorId: '', 
      donorType: '',
      grantType: '',
      budgetId: '',
      departmentId: ''
    });
  };

  const saveChanges = async () => {
    if (!editingInvoice) return;
    
    try {
      console.log('BUReview - saveChanges called with editValues:', editValues);
      
      await invoiceService.updateInvoice('bu', {
        id: editingInvoice.id,
        bu_remarks: editValues.buRemarks || undefined,
        donor_id: editValues.donorId ? editValues.donorId : undefined,
        donor_type: editValues.donorType || undefined,
        grant_type: editValues.grantType || undefined,
        budget_id: editValues.budgetId ? editValues.budgetId : undefined, // Keep as string since BudgetOption.id is string
        department_id: editValues.departmentId ? editValues.departmentId : undefined // Keep as string since DepartmentOption.id is string
      });
      
      console.log('BUReview - Invoice updated successfully');
      showSuccess('Invoice Updated', 'BU review details updated successfully.');
      refetch();
      cancelEditing();
    } catch (error) {
      console.error('BUReview - Error updating invoice:', error);
      showError('Update Failed', 'Failed to update invoice.');
    }
  };

  const lockInvoices = async (invoiceIds: number[]) => {
    try {
      console.log('BUReview - lockInvoices called with IDs:', invoiceIds);
      
      // Validate that all invoices have required fields before attempting to lock
      const invoicesToLock = invoices.filter(inv => invoiceIds.includes(inv.id));
      const invalidInvoices = invoicesToLock.filter(inv => 
        !inv.donor_id || !inv.grant_type || !inv.budget_id || !inv.department_id
      );
      
      if (invalidInvoices.length > 0) {
        const missingFields = invalidInvoices.map(inv => 
          `${inv.name}: Missing ${[
            !inv.donor_id && 'donor',
            !inv.grant_type && 'grant type', 
            !inv.budget_id && 'budget ID',
            !inv.department_id && 'department'
          ].filter(Boolean).join(', ')}`
        );
        
        showError('Validation Failed', `Cannot approve invoices with missing required fields:\n${missingFields.join('\n')}`);
        return;
      }
      
      const lockRequests = invoiceIds.map(id => ({ id }));
      console.log('BUReview - Sending lock requests:', lockRequests);
      
      await invoiceService.lockInvoices('bu', lockRequests);
      
      showSuccess('Invoices Approved', `${invoiceIds.length} invoice(s) approved successfully and sent to Finance.`);
      refetch();
      setSelectedInvoices([]);
    } catch (error: any) {
      console.error('BUReview - Lock invoices error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      showError('Approval Failed', `Failed to approve invoices: ${errorMessage}`);
    }
  };

  const handleBulkUpdate = async (field: 'donor_id' | 'grant_type' | 'budget_id' | 'department_id', value: string) => {
    console.log('BUReview - handleBulkUpdate called with:', { field, value, selectedInvoices });
    
    if (selectedInvoices.length === 0) {
      showError('No Selection', 'Please select invoices to update.');
      return;
    }

    try {
      const updateData = selectedInvoices.map(invoiceId => ({
        id: invoiceId,
        [field]: value
      }));
      
      console.log('BUReview - Update data:', updateData);
      
      const result = await invoiceService.updateInvoices('bu', updateData);
      console.log('BUReview - Update result:', result);
      
      showSuccess('Bulk Update Successful', `${field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} updated for ${selectedInvoices.length} invoice(s) successfully.`);
      refetch();
      setSelectedInvoices([]);
    } catch (error: any) {
      console.error('BUReview - Bulk update error:', error);
      console.error('BUReview - Error response:', error?.response);
      console.error('BUReview - Error data:', error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      showError('Bulk Update Failed', `Failed to update invoices: ${errorMessage}`);
    }
  };


  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Approved By PnC':
        return 'badge badge-info';
      case 'Approved By BU':
        return 'badge badge-success';
      default:
        return 'badge';
    }
  };

  const toggleInvoiceSelection = (invoiceId: number) => {
    // Allow selection of all "Approved By PnC" invoices for bulk updates
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (invoice && invoice.state !== 'Approved By PnC') {
      showError('Invalid Selection', 'Only invoices approved by PnC can be selected.');
      return;
    }
    
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    if (!invoicesData?.list) return;
    
    const approvedByPnCInvoices = invoicesData.list.filter(inv => inv.state === 'Approved By PnC');
    
    if (selectedInvoices.length === approvedByPnCInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(approvedByPnCInvoices.map(inv => inv.id));
    }
  };

  // Get donor name from config service
  const getDonorName = (donorId?: string) => {
    return configService.getDonorNameById(donorId);
  };

  // Get budget label from config service
  const getBudgetLabel = (budgetId?: string) => {
    return configService.getBudgetLabelById(budgetId);
  };

  // Get department name from config service
  const getDepartmentName = (departmentId?: string) => {
    return configService.getDepartmentNameById(departmentId);
  };

  // Get grant type label from config service
  const getGrantTypeLabel = (grantTypeId?: string) => {
    if (!grantTypeId) return 'Not set';
    const grantType = configService.getGrantTypeById(grantTypeId);
    return grantType ? grantType.label : grantTypeId;
  };

  // Check if invoice has all required fields for approval
  const hasRequiredFields = (invoice: Invoice) => {
    return invoice.donor_id && invoice.grant_type && invoice.budget_id && invoice.department_id;
  };

  // Loading and error states
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading invoices...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading invoices. Please try again.</div>
      </div>
    );
  }

  const invoices = invoicesData?.list || [];
  const approvedByPnCInvoices = invoices.filter(inv => inv.state === 'Approved By PnC');
  const canLockSelected = selectedInvoices.length > 0 && selectedInvoices.every(id => {
    const invoice = approvedByPnCInvoices.find(inv => inv.id === id);
    return invoice && invoice.donor_id && invoice.grant_type && invoice.budget_id && invoice.department_id;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            BU Review - Step 2
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Review PnC approved invoices, update donor details, grant types, budget information and department assignments.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <Button
            variant="secondary"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportData}
          >
            Export
          </Button>
          {canLockSelected && (
            <Button
              icon={<CheckCircle className="h-4 w-4" />}
              onClick={() => lockInvoices(selectedInvoices)}
            >
              Approve Selected ({selectedInvoices.length})
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Update Actions */}
      {selectedInvoices.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-rgf-gray-700">
                {selectedInvoices.length} invoice(s) selected
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedInvoices([])}
              >
                Clear Selection
              </Button>
            </div>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Bulk Grant Type Update */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-rgf-gray-700">Bulk Update Grant Type:</label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkUpdate('grant_type', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Select Grant Type</option>
                  {configService.getGrantTypes().map(grantType => (
                    <option key={grantType.id} value={grantType.id}>
                      {grantType.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bulk Donor Update */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-rgf-gray-700">Bulk Update Donor:</label>
                <select
                  onChange={(e) => {
                    console.log('Donor dropdown changed:', e.target.value);
                    if (e.target.value) {
                      handleBulkUpdate('donor_id', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Select Donor</option>
                  {configService.getDonors().map(donor => (
                    <option key={donor.id} value={donor.id}>
                      {donor.name} ({donor.type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Bulk Budget ID Update */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-rgf-gray-700">Bulk Update Budget ID:</label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkUpdate('budget_id', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Select Budget ID</option>
                  {configService.getBudgetIds().map(budget => (
                    <option key={budget.id} value={budget.id}>
                      {budget.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bulk Department Update */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-rgf-gray-700">Bulk Update Department:</label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkUpdate('department_id', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>Select Department</option>
                  {configService.getDepartments().map(department => (
                    <option key={department.id} value={department.id}>
                      {department.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or employee ID..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  startIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button 
                variant="secondary" 
                icon={<Filter className="h-4 w-4" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Collapsible Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t border-rgf-gray-200">
                {/* Month Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Month
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>

                {/* Program Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Program
                  </label>
                  <select
                    value={selectedProgram}
                    onChange={(e) => { setSelectedProgram(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {programs.map((program) => (
                      <option key={program.value} value={program.value}>
                        {program.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role Filter — parity with PnC Review per ticket "Filtering
                    Option in Backoffice in Honorarium Management". */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => { setSelectedRole(e.target.value); setCurrentPage(1); }}
                    disabled={rolesLoading}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:opacity-50"
                  >
                    {rolesLoading ? (
                      <option value="">Loading roles...</option>
                    ) : (
                      roleOptions.map((role, index) => (
                        <option key={`${role.value}-${index}`} value={role.value}>
                          {role.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* State Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => { setSelectedState(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {stateOptions.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedMonth(getCurrentMonth());
                      setSelectedProgram('');
                      setSelectedRole('');
                      setSelectedState('');
                      setSearchQuery('');
                      setCurrentPage(1);
                      setPageSize(10);
                    }}
                    className="w-full"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Table */}
      <div className="card">
        <div className="card-body p-0">
          {invoices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-rgf-gray-600">No invoices pending BU review.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    <th className="table-header w-12">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.length === approvedByPnCInvoices.length && approvedByPnCInvoices.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="table-header">Associate</th>
                    <th className="table-header">Program</th>
                    <th className="table-header">Calculated Amount</th>
                    <th className="table-header">Grant Type</th>
                    <th className="table-header">Donor</th>
                    <th className="table-header">Budget ID</th>
                    <th className="table-header">Department</th>
                    <th className="table-header">BU Remarks</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-200">
                  {invoices.map((invoice) => {
                    const isEditing = editingInvoice?.id === invoice.id;
                    const canEdit = invoice.state === 'Approved By PnC';
                    
                    const isComplete = hasRequiredFields(invoice);
                    
                    return (
                      <tr key={invoice.id} className={`hover:bg-rgf-gray-50 ${!isComplete && canEdit ? 'bg-yellow-50' : ''}`}>
                        <td className="table-cell">
                          {canEdit && (
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedInvoices.includes(invoice.id)}
                                onChange={() => toggleInvoiceSelection(invoice.id)}
                                className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-gray-300 rounded"
                              />
                              {!isComplete && (
                                <div title="Missing required fields">
                                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-rgf-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-rgf-gray-900">
                                {invoice.name}
                              </div>
                              <div className="text-sm text-rgf-gray-500">
                                {invoice.employee_id} • {invoice.designation}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="badge badge-info">{invoice.program}</span>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-rgf-gray-900">
                            {invoice.invoice_value ? `₹${invoice.invoice_value.toLocaleString()}` : '-'}
                          </div>
                          <div className="text-xs text-rgf-gray-500">
                            {invoice.attended_days + (invoice.exceptions || 0)} days
                          </div>
                        </td>
                        <td className="table-cell">
                          {isEditing ? (
                            <select
                              value={editValues.grantType}
                              onChange={(e) => {
                                const newGrantType = e.target.value;
                                setEditValues(prev => ({
                                  ...prev, 
                                  grantType: newGrantType,
                                  donorId: '', // Clear donor selection when grant type changes
                                  donorType: ''
                                }));
                              }}
                              className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select Grant</option>
                              {configService.getGrantTypes().map(grantType => (
                                <option key={grantType.id} value={grantType.id}>
                                  {grantType.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-sm text-rgf-gray-900">
                              {getGrantTypeLabel(invoice.grant_type)}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          {isEditing ? (
                            <select
                              value={editValues.donorId}
                              onChange={(e) => {
                                const selectedDonor = configService.getDonorById(e.target.value.trim());
                                setEditValues(prev => ({
                                  ...prev, 
                                  donorId: e.target.value,
                                  donorType: selectedDonor?.type || ''
                                }));
                              }}
                              className="w-40 px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select Donor</option>
                              {/* Filter donors by selected grant type */}
                              {editValues.grantType ? 
                                configService.getDonorsByGrantType(editValues.grantType).map(donor => (
                                  <option key={donor.id} value={donor.id}>
                                    {donor.name}
                                  </option>
                                ))
                                :
                                configService.getDonors().map(donor => (
                                  <option key={donor.id} value={donor.id}>
                                    {donor.name} ({donor.type})
                                  </option>
                                ))
                              }
                            </select>
                          ) : (
                            <div className="text-sm text-rgf-gray-900">
                              <div>{getDonorName(invoice.donor_id)}</div>
                              {invoice.donor_type && (
                                <div className="text-xs text-rgf-gray-500">{invoice.donor_type}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          {isEditing ? (
                            <select
                              value={editValues.budgetId}
                              onChange={(e) => setEditValues(prev => ({...prev, budgetId: e.target.value}))}
                              className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select Budget</option>
                              {configService.getBudgetIds().map(budget => (
                                <option key={budget.id} value={budget.id}>
                                  {budget.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-sm text-rgf-gray-900">
                              {getBudgetLabel(invoice.budget_id)}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          {isEditing ? (
                            <select
                              value={editValues.departmentId}
                              onChange={(e) => setEditValues(prev => ({...prev, departmentId: e.target.value}))}
                              className="w-40 px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select Department</option>
                              {configService.getDepartments().map(department => (
                                <option key={department.id} value={department.id}>
                                  {department.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-sm text-rgf-gray-900">
                              {getDepartmentName(invoice.department_id)}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editValues.buRemarks}
                              onChange={(e) => setEditValues(prev => ({...prev, buRemarks: e.target.value}))}
                              className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Add remarks"
                            />
                          ) : (
                            <div className="text-sm text-rgf-gray-500">
                              {invoice.bu_remarks || '-'}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={getStatusBadgeClass(invoice.state || 'Approved By PnC')}>
                            {invoice.state || 'Approved By PnC'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            {isEditing ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={saveChanges}
                                  className="text-green-600 hover:text-green-800"
                                  icon={<Save className="h-3 w-3" />}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelEditing}
                                  className="text-gray-600 hover:text-gray-800"
                                  icon={<X className="h-3 w-3" />}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : canEdit ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => startEditing(invoice)}
                                  className="text-blue-600 hover:text-blue-800"
                                  icon={<Edit2 className="h-3 w-3" />}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => lockInvoices([invoice.id])}
                                  disabled={!isComplete}
                                  className={`${isComplete ? 'text-green-600 hover:text-green-800' : 'text-gray-400 cursor-not-allowed'}`}
                                  icon={<CheckCircle className="h-3 w-3" />}
                                  title={!isComplete ? 'Please fill in all required fields first' : 'Approve this invoice'}
                                >
                                  Approve
                                </Button>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">
                                {invoice.state === 'Approved By BU' && 'Sent to Finance'}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {invoicesData && invoicesData.list.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing page {invoicesData.page} with {invoicesData.list.length} records
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Page size:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {pageSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={invoicesData.list.length < pageSize}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};